// useConflictAwareUndo — conflict-aware Undo の UI 側配線 (最後のピース)。
//
// (docs/_wip/undo-conflict-aware-2026-07-12.md「設計確定」節 + 「Undo 拒否時の UI —
// 初期スコープに昇格」 / protocol-events.md §1.1 が正)
//
// engine 側 (B'、commit 390cd61) が持つ「自分の cancel 可能な stroke の LIFO 提示順
// スタック」(`getCancellableStrokes` / `onCancellableStrokesChange`) と拒否通知
// (`onCancelRejected`) を購読し、Undo ボタン/ショートカットが使う最小 API に絞る:
//   - `canUndo`      : スタックが空でないか (ボタンの enable/disable)。
//   - `triggerUndo()`: スタック先頭 (直近) が線なら sid を `sendCancelStroke` に、範囲の移動・コピー
//     なら seq を `sendCancelRegion` に渡す (線と移動は同じ一覧に混ざる)。
//     連打 / 短時間の Ctrl+Z リピートでも **in-flight を理由に disable しない**
//     (undo-conflict-aware-2026-07-12.md「3. 連打対応」— サーバ判定が正、engine 側の
//     coalescing が受け側を守る)。毎回 `getCancellableStrokes()` を読み直すので、
//     直前の cancel が承認され `stroke.cancelled` が反映済みなら次のクリックは
//     自動的にその次の stroke を対象にする (拒否時は反映されないので同じ sid を
//     再送する — サーバが引き続き判定する、これも「サーバ判定が正」の帰結)。
//   - `rejectToast`  : 拒否 (`stroke.cancel-rejected`) のトースト state。表示は
//     呼び出し側が `ImagepasteToastOverlay` (キャンバス中央・約 3 秒自動消滅の
//     既存流儀、useImagepaste.ts と同型) を再利用して描く。
//
// Classic/Modern 共通で配線する (2026-07-23 是正: modern-layout.md §1.1 表現パリティ
// 「機能を Classic/Modern でゲートしない」に沿い、当初 Modern 限定だった穴を是正。
// このフック自体は元々レイアウト非依存 — Modern 限定だったのは呼び出し側の配線のみ。
// Classic は ToolPanel ボタンを新設せず、ショートカット (`edit.undo`) + 拒否
// トースト/ハイライトの直接描画が受け皿 (texture-brush-design.md §10.2 と同型)。
//
// bbox ハイライト (2026-07-22 スコープ判断で一旦見送り → 本タスクで実装):
// `reason='conflict'` の拒否は表示名トースト (`undoRejectText.ts`) に加え、conflicts の
// 各 stroke の bbox を `engine.getUndoConflictBbox` (= `CanvasDrawingEngine` が
// `UndoBaselineStore.getStrokeBbox` を薄くラップした読み取り専用 accessor、
// docs/_wip/undo-conflict-aware-2026-07-12.md「B' 実装での追加事項」) で解決し、
// トーストと同時に ~3 秒キャンバス上へハイライト表示する
// (`UndoConflictHighlightOverlay`、`CanvasViewMapper` (viewport.ts §1b) の座標変換 —
// `ImagepasteOverlay`/`ImagepasteToastOverlay` と同じ流儀 — を再利用)。bbox を解決
// できない sid (未知 / 描きかけで bbox 未確定) は静かにスキップする (undefined を
// フィルタするだけ、エラーにしない)。

import type { Rect } from '@oekaki/drawing-core';
import type { StrokeCancelRejected, WhiteboardRegionCancelRejected } from '@oekaki/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CancellableOp } from '../../application/undoOps.js';
import { undoRejectText } from '../../application/undoRejectText.js';
import { createUndoBadgeSeenStore } from '../../infrastructure/undoBadgeSeen.js';
import type { ImagepasteToast as CanvasCenterToast } from '../layouts/classic/ImagepasteToastOverlay.js';
import { useConnectionLocked } from '../providers/ConnectionLockContext.js';

/**
 * engine が提供する conflict-aware Undo の最小 surface。`CanvasDrawingEngine`
 * (infrastructure/drawing/CanvasDrawingEngine.ts) が構造的に満たす — ローカル定義に
 * するのはテストが最小スタブで代替できるようにするため (useImagepaste の
 * `ImagepasteEngine` と同方針)。
 */
export interface ConflictAwareUndoEngine {
  /** 自分の取り消せる操作 (線と範囲の移動・コピー) を新しい順に。 */
  getCancellableOps(): readonly CancellableOp[];
  onCancellableOpsChange(callback: (stack: readonly CancellableOp[]) => void): () => void;
  onCancelRejected(
    callback: (payload: StrokeCancelRejected | WhiteboardRegionCancelRejected) => void,
  ): () => void;
  /** bbox ハイライト用の読み取り専用 accessor。未知/未確定 sid は `undefined`。 */
  getUndoConflictBbox(sid: string): Rect | undefined;
  /** 範囲の移動・コピーの元と置き先の矩形 (ハイライト用)。未追跡は空配列。 */
  getUndoRegionRects(regionSeq: number): Rect[];
  /**
   * 描いている最中の自分の線の sid (省略 = 常に空)。描き途中の「元に戻す」は、その線を描き終えて
   * 取り消し候補に積まれてから送る (描き途中の自分の線と重なる 1 本前の線を cancel して、サーバに
   * 「上に描いている」と拒否されるのを避ける)。
   */
  getActiveOwnStrokeSids?(): readonly string[];
  /** その線がまだ「取り消せる」前か (描き途中 + echo 待ち)。false になったら待ちを捨てる。 */
  isOwnStrokePending?(sid: string): boolean;
}

/** 描き終えるのを待つ「元に戻す」を捨てる上限 (ms)。通常は線を描き終えた直後の echo で解決する。 */
export const UNDO_AFTER_STROKE_MAX_WAIT_MS = 60_000;

/**
 * 拒否トースト/ハイライトの表示時間 (ms)。2026-10-26: 子どもが読んで気づく前に消えて
 * 「バグ」に見えていたため 3 秒 → 5 秒に延長 (undo-conflict-feedback-2026-10-26.md)。
 * トースト側は `ImagepasteToastOverlay` の `autoDismissMs` に `UNDO_REJECT_DISPLAY_MS` を渡す。
 */
export const UNDO_REJECT_DISPLAY_MS = 5_000;
const HIGHLIGHT_TOTAL_MS = UNDO_REJECT_DISPLAY_MS;
/** フェードアウトに使う末尾区間 (ms)。`fading=true` になってから実際に消えるまで。 */
const HIGHLIGHT_FADE_MS = 400;

/** 一時ハイライトする 1 矩形 (交差 stroke 1 本分)。 */
export interface UndoConflictHighlightBox {
  /** 拒否イベントごとの世代 id (= 同じ拒否で出た矩形をまとめてフェード/消去する)。 */
  readonly id: number;
  readonly bbox: Rect;
  /** true の間、呼び出し側 (`UndoConflictHighlightOverlay`) は CSS でフェードアウトする。 */
  readonly fading: boolean;
}

export interface UseConflictAwareUndoArgs {
  /** `chrome.engine` (= CanvasDrawingEngine)。canvas 未マウント時は null。 */
  readonly engine: ConflictAwareUndoEngine | null;
  /** `useSession().sendCancelStroke` (= `stroke.cancel` 送信)。 */
  readonly sendCancelStroke: (sid: string) => void;
  /** `useSession().sendCancelRegion` (= `whiteboard.region-cancel` 送信)。 */
  readonly sendCancelRegion: (regionSeq: number) => void;
  /**
   * 範囲の移動・コピーを浮かせている最中 (確定前) の扱い。浮かせている間の「元に戻す」は、
   * 下にある線や移動を取り消さず、[やめる] と同じ (浮かせた分を捨てて選択に戻る) にする。
   * 確定前の移動はまだ誰にも送られておらず、浮かせた絵は取り込み済みの写しなので、下の絵を
   * 戻すと写しと食い違うため。未指定は「浮かせていない」。
   */
  readonly floatingRegion?: {
    readonly isFloating: boolean;
    readonly cancelFloating: () => void;
  };
  /** `conflicts[].uid` → 表示名 (members から解決、自分自身も渡り得る)。 */
  readonly resolveDisplayName: (uid: string) => string | null;
}

export interface UseConflictAwareUndoReturn {
  /** Undo ボタンの enable/disable (= cancel 可能な自分の stroke が 1 本以上あるか)。 */
  readonly canUndo: boolean;
  /** ボタンクリック / Ctrl+Z で呼ぶ。スタックが空 (= engine null 含む) なら no-op。 */
  readonly triggerUndo: () => void;
  /** 拒否トースト (キャンバス中央表示用)。null で非表示。 */
  readonly rejectToast: CanvasCenterToast | null;
  /** 静的文言トーストの自動消滅を反映する (= ImagepasteToastOverlay の onDismiss)。 */
  readonly dismissRejectToast: () => void;
  /**
   * bbox ハイライト矩形 (= `reason='conflict'` の conflicts のうち bbox を解決
   * できた分)。拒否トーストと同時に立ち、~3 秒で `fading:true` を経て自動的に
   * 消える (`UndoConflictHighlightOverlay` が描画)。conflict 以外の reason や
   * bbox 未解決の conflicts しか無かった場合は空配列。
   */
  readonly highlightBoxes: ReadonlyArray<UndoConflictHighlightBox>;
  /**
   * Undo ボタンに「New!」バッジを出すか (2026-07-23 追補、undo-conflict-aware-2026-07-12.md)。
   * 初回に `triggerUndo()` が実際に cancel を送信した時点で false へ切り替わる
   * (ボタン押下 / Ctrl+Z ショートカットどちらの経路でも同じ関数を通るので、この hook
   * 1 箇所で両経路・Classic/Modern 両レイアウトに効く — undoBadgeSeen.ts が保持する
   * 単一の localStorage key を Classic/Modern 双方のフックインスタンスが参照するため、
   * 片方で消せばもう片方でも消える)。
   */
  readonly showBadge: boolean;
}

export const useConflictAwareUndo = (
  args: UseConflictAwareUndoArgs,
): UseConflictAwareUndoReturn => {
  const { engine, sendCancelStroke, sendCancelRegion, resolveDisplayName, floatingRegion } = args;
  const floatingRegionRef = useRef(floatingRegion);
  floatingRegionRef.current = floatingRegion;
  // 再接続中は Undo も受け付けない (docs/_wip/reconnect-draw-lock-2026-10-12.md)。
  const connectionLocked = useConnectionLocked();
  const connectionLockedRef = useRef(connectionLocked);
  connectionLockedRef.current = connectionLocked;

  const [stack, setStack] = useState<readonly CancellableOp[]>(
    () => engine?.getCancellableOps() ?? [],
  );
  const [rejectToast, setRejectToast] = useState<CanvasCenterToast | null>(null);
  const [highlightBoxes, setHighlightBoxes] = useState<ReadonlyArray<UndoConflictHighlightBox>>([]);
  const toastIdRef = useRef(0);
  // 直近の拒否 (連打判定用)。同じ操作が表示中に続けて拒否されたら出し直さず延長する。
  const lastRejectRef = useRef<{ key: string; at: number; generation: number } | null>(null);
  // 「New!」バッジの seen 永続化 (2026-07-23 追補)。Classic/Modern 共有の 1 本の
  // localStorage key (undoBadgeSeen.ts、timelapseShareSeen.ts と同流儀)。
  const undoBadgeSeenStoreRef = useRef(createUndoBadgeSeenStore());
  const [showBadge, setShowBadge] = useState<boolean>(
    () => !undoBadgeSeenStoreRef.current.hasSeen(),
  );
  // 進行中の fade/clear タイマー (= 新しい拒否が来たら前回分は破棄する。世代 id で
  // 対象を絞っているので破棄漏れがあっても実害は無いが、明示的に片付ける)。
  const highlightTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const clearHighlightTimers = useCallback((): void => {
    for (const t of highlightTimersRef.current) clearTimeout(t);
    highlightTimersRef.current = [];
  }, []);

  // `resolveDisplayName` は呼び出し側で `state.members` から都度組み立てられがちな
  // closure なので、購読 (onCancelRejected) の re-subscribe を避けるため ref 越しに
  // 最新を読む (useImagepaste の `pasteLayerIdRef` と同方針)。
  const resolveDisplayNameRef = useRef(resolveDisplayName);
  resolveDisplayNameRef.current = resolveDisplayName;
  const sendCancelStrokeRef = useRef(sendCancelStroke);
  sendCancelStrokeRef.current = sendCancelStroke;
  const sendCancelRegionRef = useRef(sendCancelRegion);
  sendCancelRegionRef.current = sendCancelRegion;

  // 描き途中に押された「元に戻す」の待ち (その線の sid)。1 回分だけ持つ。
  const pendingUndoSidRef = useRef<string | null>(null);
  const pendingUndoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPendingUndo = useCallback((): void => {
    pendingUndoSidRef.current = null;
    if (pendingUndoTimerRef.current !== null) {
      clearTimeout(pendingUndoTimerRef.current);
      pendingUndoTimerRef.current = null;
    }
  }, []);

  const sendTop = useCallback((top: CancellableOp): void => {
    // 「New!」バッジ (2026-07-23 追補): 実際に cancel を試みた時点で「機能の存在を
    // 認知した」とみなし seen 化する。サーバの応答 (承認/拒否) を待たない — 拒否
    // (conflict / too-old 等) でも押した事実は変わらないため、undoBadgeSeen.ts の
    // 仕様どおり消したままにする。
    undoBadgeSeenStoreRef.current.markSeen();
    setShowBadge(false);
    // 先頭が範囲の移動・コピーなら region-cancel、線なら stroke.cancel。取り消し済みへの
    // 再要求は server が無応答だが、ここは待ち状態を持たない (cancelled の受信で一覧から外れ、
    // 次の押下は次の操作を対象にする)。
    if (top.kind === 'region') sendCancelRegionRef.current(top.regionSeq);
    else sendCancelStrokeRef.current(top.sid);
  }, []);

  // スタック購読: engine の (re)mount ごとに再購読 + 現在値を即座に反映する。
  useEffect(() => {
    if (engine === null) {
      setStack([]);
      clearPendingUndo();
      return;
    }
    setStack(engine.getCancellableOps());
    const off = engine.onCancellableOpsChange((next) => {
      setStack(next);
      // 描き途中に押された「元に戻す」: その線が取り消し候補に積まれたら、その線を戻す。
      // 積まれないまま待ちの対象でなくなった (成立しなかった / 再接続・再読み込みでリセット) 場合は捨てる。
      const sid = pendingUndoSidRef.current;
      if (sid === null) return;
      const op = next.find((o) => o.kind === 'stroke' && o.sid === sid);
      if (op !== undefined) {
        clearPendingUndo();
        if (!connectionLockedRef.current) sendTop(op);
      } else if (engine.isOwnStrokePending?.(sid) !== true) {
        clearPendingUndo();
      }
    });
    return () => {
      off();
      clearPendingUndo();
    };
  }, [engine, clearPendingUndo, sendTop]);

  // 切断・再接続中になったら待ちは捨てる (何も送らない)。
  useEffect(() => {
    if (connectionLocked) clearPendingUndo();
  }, [connectionLocked, clearPendingUndo]);

  // 拒否通知の購読 (トースト + bbox ハイライト、同じイベントから両方組み立てる)。
  useEffect(() => {
    if (engine === null) {
      setHighlightBoxes([]);
      clearHighlightTimers();
      return;
    }
    const off = engine.onCancelRejected((payload) => {
      // 連打対応: 拒否されても次の押下は同じ線を再送する (仕様)。同じ操作・同じ reason の
      // 拒否が表示中に続いたら、トースト/ハイライトは出し直さず (= チカチカさせず) 表示時間だけ
      // 延長する。トーストは id を進めるだけ (同じ DOM のまま overlay の消滅タイマーが貼り直される)。
      const key = `${payload.t}:${payload.t === 'stroke.cancel-rejected' ? payload.sid : payload.regionSeq}:${payload.reason}`;
      const now = Date.now();
      const last = lastRejectRef.current;
      const isRepeat = last !== null && last.key === key && now - last.at < HIGHLIGHT_TOTAL_MS;
      toastIdRef.current += 1;
      const generation = isRepeat && last !== null ? last.generation : toastIdRef.current;
      lastRejectRef.current = { key, at: now, generation };
      setRejectToast({
        text: undoRejectText(payload, resolveDisplayNameRef.current),
        id: toastIdRef.current,
        countdown: false,
      });

      // bbox ハイライト: `conflict` のみ、accessor で解決できた分だけ (未解決 sid は
      // 静かにスキップ — 未知 / 描きかけ stroke の成長中 bbox は追えない、f2)。
      clearHighlightTimers();
      const boxes: UndoConflictHighlightBox[] = [];
      if (payload.reason === 'conflict') {
        for (const c of payload.conflicts ?? []) {
          const bbox = engine.getUndoConflictBbox(c.sid);
          if (bbox !== undefined) boxes.push({ id: generation, bbox, fading: false });
        }
        // 範囲の移動・コピーの取り消しで、重なった線の bbox が出せない (conflicts が空 =
        // 線以外の画像貼り付け・別の移動・層クリア由来など) ときは、取り消そうとした移動の
        // 元と置き先の矩形を出す。「この辺りが邪魔をしている」の手がかりにする。
        if (boxes.length === 0 && payload.t === 'whiteboard.region-cancel-rejected') {
          for (const bbox of engine.getUndoRegionRects(payload.regionSeq)) {
            boxes.push({ id: generation, bbox, fading: false });
          }
        }
      }
      setHighlightBoxes((prev) =>
        // 連打の延長時は、すでに出ている同じ世代の矩形を (フェード中なら戻して) 維持する。
        isRepeat && prev.length > 0 ? prev.map((b) => ({ ...b, fading: false })) : boxes,
      );
      if (boxes.length === 0) return;
      // トーストと同程度 ~3 秒 (`ImagepasteToastOverlay` の既定 autoDismissMs と
      // 揃える)。末尾 `HIGHLIGHT_FADE_MS` で fading フラグを立て、overlay 側の CSS
      // transition でフェードアウトさせてから配列を空にする。
      const fadeTimer = setTimeout(() => {
        setHighlightBoxes((prev) =>
          prev.map((b) => (b.id === generation ? { ...b, fading: true } : b)),
        );
      }, HIGHLIGHT_TOTAL_MS - HIGHLIGHT_FADE_MS);
      const clearTimer = setTimeout(() => {
        setHighlightBoxes((prev) => prev.filter((b) => b.id !== generation));
      }, HIGHLIGHT_TOTAL_MS);
      highlightTimersRef.current = [fadeTimer, clearTimer];
    });
    return () => {
      off();
      clearHighlightTimers();
    };
  }, [engine, clearHighlightTimers]);

  const triggerUndo = useCallback((): void => {
    if (engine === null) return;
    if (connectionLockedRef.current) return;
    // 浮かせている最中は [やめる] と同じ (取り消しは送らない)。
    const floating = floatingRegionRef.current;
    if (floating?.isFloating === true) {
      floating.cancelFloating();
      return;
    }
    // 自分の線を描いている最中: 今は送らず、その線を描き終えて取り消し候補に積まれてから
    // その線を戻す (1 本前の線を cancel すると、描き途中の自分の線に「上に描かれた」と拒否される)。
    // 待つのは 1 回分だけ (待ち中の追加の押下は無視)。
    if (pendingUndoSidRef.current !== null) return;
    const drawingSid = engine.getActiveOwnStrokeSids?.()[0];
    if (drawingSid !== undefined) {
      pendingUndoSidRef.current = drawingSid;
      pendingUndoTimerRef.current = setTimeout(clearPendingUndo, UNDO_AFTER_STROKE_MAX_WAIT_MS);
      return;
    }
    // 毎回読み直す (= 連打対応。前回の cancel が承認済みならスタック先頭が進んでいる)。
    const top = engine.getCancellableOps()[0];
    if (top === undefined) return;
    sendTop(top);
  }, [engine, clearPendingUndo, sendTop]);

  const dismissRejectToast = useCallback((): void => {
    setRejectToast((prev) => (prev !== null && !prev.countdown ? null : prev));
  }, []);

  return {
    canUndo: (stack.length > 0 || floatingRegion?.isFloating === true) && !connectionLocked,
    triggerUndo,
    rejectToast,
    dismissRejectToast,
    highlightBoxes,
    showBadge,
  };
};
